import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  blobToBase64String,
  buildMobileAppDeepLink,
  getSubdomain,
  handleError,
  isInMobileApp,
} from "@/common/utils/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

// Local imports
import img from "@/assets/img/theme/no-image.png";
import { formSchema } from "../../shared/schemas/definePasswordFormSchema";
import { DefinePasswordFormModel } from "../../shared/models/models";
import {
  getTenantLogoBySubdomain,
  resetUserPassword,
  verifyUserEmail,
} from "@/services/auth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Logo } from "@/common/components/atoms/logo/logo";
import { LockOutlined } from "@mui/icons-material";
import { EvesTextbox } from "@/common/components/atoms/textbox/textbox";
import { EvesButton } from "@/common/components/atoms/button/button";
import { Grid, IconButton, InputAdornment } from "@mui/material";
import { showNotification } from "@/store/app/reducer";
import store from "@/store";

import styles from "./definePassword.module.scss";
import { RestResponse } from "@/common/enums/enums";

import { PasswordPolicyChecklist } from "@/common/components/molecules/passwordPolicyChecklist/passwordPolicyChecklist";

const DefinePassword = () => {
  const { t: translate } = useTranslation();
  const [state, setStateData] = useState({
    tenantLogo: img,
    isPasswordVisible: false,
    isRepeatPasswordVisible: false,
  });

  const navigate = useNavigate();
  const subDomain: any = getSubdomain();

  const search = useLocation().search;
  const resetTokenHash: any = new URLSearchParams(search).get("ResetToken");
  const verificationToken = new URLSearchParams(search).get(
    "VerificationToken",
  );
  const userName = new URLSearchParams(search).get("username");
  const userDomain = new URLSearchParams(search).get("userdomain");
  const confirmationHash = new URLSearchParams(search).get("confirmation");
  // to set URL for mobile user
  let currentWindowURL: any = window.location.href;

  const { control, formState, getValues, setValue, trigger, watch } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: new DefinePasswordFormModel(),
    mode: "onTouched",
  });


  const watchPassword = watch("password", ""); // use watch to get real-time value
  const watchRepeatPassword = watch("repeatPassword", ""); // use watch to get real-time value

  const { errors, isValid } = formState;
  const canSubmitForm = isValid;

  const { refetch: fetchTenantLogo } = useQuery(
    "tenantLogo",
    () => {
      if (subDomain) {
        return getTenantLogoBySubdomain(subDomain);
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: async (data: any) => {
        if (data) {
          const base64String = await blobToBase64String(data);
          setStateData((currentData) => {
            return {
              ...currentData,
              tenantLogo: base64String,
            };
          });
        }
      },
    }
  );

  const verifyEmail = (data: any) => {
    verifyUserEmail({
      Email: data.email,
      VerificationToken: data.verificationToken,
      tenant: subDomain || "",
    })
      .then((response: any) => {
        if (response.status && response.status === RestResponse.SUCCESS) {
          // Go to login
          navigate("/login");
        } else {
          // Unexpected error
          handleError(
            JSON.stringify(response),
            translate("authentication.verify_email_error") || "",
          );
        }
      })
      .catch(() => {
        navigate("/login");
      });
  };

  useEffect(() => {
    fetchTenantLogo();
  }, []);

  // set URL params into form controls so they are sent with reset
  useEffect(() => {
    if (userName) setValue("email", userName);
    if (userDomain) setValue("tenant", userDomain);
    if (confirmationHash) setValue("resetPasswordHash", confirmationHash);
  }, [userName, userDomain, confirmationHash, setValue]);

  // password visibility toggle
  const handleClickShowPassword = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isPasswordVisible: !currentData.isPasswordVisible,
      };
    });
  };

  const handleClickShowRepeatPassword = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isRepeatPasswordVisible: !currentData.isRepeatPasswordVisible,
      };
    });
  };

  const resetPassword = (data: any) => {
    // EVES-2842 - password reset fixes
    // const userHash = verificationEmail ? resetTokenHash : hashID;
    // data["resetPasswordHash"] = userHash;
    // setValue("resetPasswordHash", userHash);
    const resetForm = getValues();

    // Handle Deep Linking
    if (isInMobileApp(subDomain)) {
      // Forward to Mobile App
      const mobileAppURL = buildMobileAppDeepLink(
        `resetPassword/${subDomain}/${resetTokenHash}`,
      );
      currentWindowURL = mobileAppURL;
    }

    resetUserPassword(
      resetForm.password,
      resetForm.repeatPassword,
      resetForm.resetPasswordHash,
      resetForm.email,
      resetForm.tenant,
    ).then((res: any) => {
      // show notification when password changed successfully
      if (res.status === "Success") {
        store.dispatch(
          showNotification({
            showNotification: true,
            message: `${translate("authentication.define_password_success")}`,
            type: "success",
          }),
        );
        // re-direct user to login screen after successful password change
        if (verificationToken) {
          verifyEmail({
            email: resetForm.email,
            verificationToken: verificationToken,
          });
        } else {
          navigate("/login");
        }
      } else {
        // show notification when encoutered error
        store.dispatch(
          showNotification({
            showNotification: true,
            message: `${translate("authentication.define_password_error")}`,
            type: "error",
          }),
        );
      }
    });
  };

  const validatePasswords = () => {
    trigger("password");
    if (formState.touchedFields["repeatPassword"]) trigger("repeatPassword");
  };

  return (
    <div className={styles.register}>
      <div className={styles.registerCard}>
        <div className={styles.registerCardHeader}>
          <Logo src={state.tenantLogo} alt="tenant-logo" />
        </div>
        <h4 className={styles.registerTitle}>{`${translate(
          "authentication.define_password_title",
        )}`}</h4>

        <form id="reset-password" data-cy="reset-password-main">
          <div className={styles.registerFormRow}>
            <Controller
              name="password"
              control={control}
              key="password"
              render={({ field }) => (

                <Grid className={styles.registerFormControl} container wrap="nowrap" alignItems="flex-start">
                  <LockOutlined className={styles.signup_icons} style={{ marginTop: '16px', marginRight: '10px' }} />
                  <Grid item xs sx={{ display: 'flex', flexDirection: 'column' }}>
                    <EvesTextbox
                      {...field}
                      id="password"
                      label={`${translate("authentication.password")}`}
                      type={state.isPasswordVisible ? "text" : "password"}
                      fullWidth
                      required={true}
                      variant="standard"
                      error={!!errors?.password}
                      onChange={(event: any) => {
                        field.onChange(event);
                        validatePasswords();
                      }}
                      autoFocus
                      // helperText={`${translate(
                      //   errors?.password?.message || " ",
                      // )}`}
                      // Removing previous helperText here because i am adding the detailed checklist below
                      //  helperText={errors?.password ? `${translate(errors.password.message || "")}` : " "}
                      helperText=""
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <IconButton onClick={handleClickShowPassword}>
                              {!state.isPasswordVisible ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {watchPassword ? <PasswordPolicyChecklist password={watchPassword} /> : null}
                  </Grid>
                </Grid>
              )}
            />
          </div>
          <div
            className={
              styles.registerFormRow + " " + styles.registerFormLastRow
            }
          >
            <Controller
              name="repeatPassword"
              control={control}
              key="repeatPassword"
              render={({ field }) => (
                <div className={styles.registerFormControl}>
                  <LockOutlined className={styles.registerIconInvisible} />
                  <Grid item xs sx={{ display: 'flex', flexDirection: 'column' }}>

                    <EvesTextbox
                      {...field}
                      id="repeatPassword"
                      label={`${translate("authentication.repeat_password")}`}
                      type={state.isRepeatPasswordVisible ? "text" : "password"}
                      fullWidth
                      required={true}
                      variant="standard"
                      error={!!errors?.repeatPassword}
                      // helperText={`${translate(
                      //   errors?.repeatPassword?.message || " ",
                      // )}`}
                        helperText={
                        watchRepeatPassword ? (
                          watchPassword === watchRepeatPassword ? (
                            // Success Message in Green
                            <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                              {translate("authentication.password_matched")}
                            </span>
                          ) : (
                            // Error Message (Default red via the 'error' prop above)
                            translate("authentication.password_not_matched")
                          )
                        ) : (
                          " " //  layout spacing when empty
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <IconButton onClick={handleClickShowRepeatPassword}>
                              {!state.isRepeatPasswordVisible ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {/* {watchRepeatPassword ? <PasswordPolicyChecklist password={watchRepeatPassword} /> : null} */}
                  </Grid>
                </div>
              )}
            />
          </div>
          <div className={styles.recaptcha_text}>
            {`${translate("general.captcha_text_1")}`}{" "}
            <a href="https://policies.google.com/privacy">{`${translate(
              "general.captcha_text_2",
            )}`}</a>{" "}
            {`${translate("general.captcha_text_3")}`}{" "}
            <a href="https://policies.google.com/terms">{`${translate(
              "general.captcha_text_4",
            )}`}</a>
            {`${translate("general.captcha_text_5")}`}
          </div>
          <div className={styles.registerCenterRegBtn}>
            <EvesButton
              data-cy="reset-password"
              size="medium"
              onClick={resetPassword}
              disabled={!canSubmitForm}
              id="btnLogin"
              variant="primary"
            >
              {`${translate("general.save")}`}
            </EvesButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DefinePassword;
